import { useState, type JSX, type ReactNode } from 'react';
import { v2, ApiError, type Document } from '@/api/v2Client';

export interface DocumentReorderListProps {
  submissionId: string;
  documents: Document[];
  onReordered?: (newOrder: Document[]) => void;
  renderRow?: (doc: Document) => ReactNode;
}

/**
 * Drag-and-drop list of documents. Uses the HTML5 native drag-and-drop API
 * (no new dependency). On drop, the new order is sent to the backend and
 * applied optimistically — if the request fails, the original order is
 * restored and the error surfaced to the user.
 */
export function DocumentReorderList({
  submissionId,
  documents,
  onReordered,
  renderRow,
}: DocumentReorderListProps): JSX.Element {
  const [items, setItems] = useState<Document[]>(documents);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Sync external updates.
  if (documents !== items && documents.length !== items.length) {
    setItems(documents);
  }

  const persist = async (newItems: Document[]): Promise<void> => {
    const previous = items;
    setItems(newItems);
    setError(null);
    try {
      await v2.documentReorder.reorder(
        submissionId,
        newItems.map((d) => d.Id),
      );
      onReordered?.(newItems);
    } catch (err) {
      setItems(previous);
      setError(err instanceof ApiError ? err.message : 'Reorder failed');
    }
  };

  const handleDrop = (toIndex: number): void => {
    if (dragIndex === null || dragIndex === toIndex) {
      setDragIndex(null);
      return;
    }
    const next = [...items];
    const [moved] = next.splice(dragIndex, 1);
    if (!moved) return;
    next.splice(toIndex, 0, moved);
    setDragIndex(null);
    void persist(next);
  };

  return (
    <div>
      {error && (
        <div role="alert" style={{ color: '#b91c1c', marginBottom: 8 }}>
          {error}
        </div>
      )}
      <ul aria-label="Document list (drag to reorder)" style={{ listStyle: 'none', padding: 0 }}>
        {items.map((doc, i) => (
          <li
            key={doc.Id}
            draggable
            onDragStart={() => setDragIndex(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => handleDrop(i)}
            data-testid={`doc-row-${doc.Id}`}
            style={{
              padding: 10,
              marginBottom: 6,
              border: '1px solid #e5e7eb',
              borderRadius: 6,
              background: dragIndex === i ? '#eef2ff' : '#fff',
              cursor: 'grab',
            }}
          >
            {renderRow ? renderRow(doc) : doc.FileName}
          </li>
        ))}
      </ul>
    </div>
  );
}
